
import { Ref, ref, watch } from "vue";
import { computed, ComputedRef } from "@vue/reactivity";
import { PasswordStrength } from "../../../../../domain/entities/entities";
import { useStore } from "vuex";
import {
  isCirculoTemplate,
  isConstrucolorTemplate,
  isDecolaTemplate
} from "../../mixins";

const __default__ = {
  name: "inputPassword",
  props: {
    /** It is a pointer to input and is the input's name too (this will be visible by users). */
    label: {
      required: true,
      type: String
    },

    /** Used to make a connection with another variable outside this component */
    modelValue: {
      required: false,
      default: ""
    },

    /** Prop used to set if the password view format is standard. */
    passwordVisibility: {
      required: false,
      type: Boolean
    },

    /** Prop used to set a different password view type than the default. */
    typeInput: {
      type: String,
      required: false,
      default: ""
    },
    inputColor: {
      type: String,
      default: "var(--primary-color)"
    },
    required: {
      required: false,
      type: Boolean
    }
  },

  setup(props, { emit }) {
    const store = useStore();

    /** Key that references the timer in progress. Used to cancel the timeout. */
    let timerId;

    /** Regex expression to validate a strong password.
     * Strong password pattern:
     * Special character (!@#$%^&*).
     * Upper case.
     * Lower case.
     * At least one number.
     * At least 8 characters.*/
    const strengthRegex: RegExp = new RegExp(
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})"
    );

    /** Used to control the label state. */
    const isInputFocused: Ref<boolean> = ref(false);

    const defaultColor = props.inputColor as string;
    const successColor = "#008000";
    const invalidColor = "#f4271c";

    /** Variable used to set the input color according to validation.
     * the variable is started with the default color of the inputs. */
    const inputColor: Ref<string> = ref(defaultColor);

    /** Variable used to change the span color if the password has white spaces. */
    const noWhiteSpaceValidationPassed: Ref<boolean> = ref(false);

    /** Variable used to change the span color if the password is eight characters long. */
    const eightCharacterValidationPassed: Ref<boolean> = ref(false);

    /** Variable used to change the color of the span if the password has an uppercase letter. */
    const upperCaseValidationPassed: Ref<boolean> = ref(false);

    /** Variable used to change the color of the span if the password has a lowercase letter. */
    const lowerCaseValidationPassed: Ref<boolean> = ref(false);

    /** Variable used to change the span color if the password has a number. */
    const numericCharacterValidationPassed: Ref<boolean> = ref(false);

    /** Variable used to change the span color if the password has a special character. */
    const specialCharacterValidationPassed: Ref<boolean> = ref(false);

    /** Variable that makes the password visible. */
    const isPasswordVisible: Ref<boolean> = ref(false);

    /** Computed that returns the color of the input when it has focus and without. */
    const color: ComputedRef<string> = computed(() => {
      return isInputFocused.value ? inputColor.value : defaultColor;
    });

    /** Computed which returns the strength of the user's password. */
    const passwordStrength: ComputedRef<PasswordStrength> = computed(() => {
      return store.getters["authentication_module/passwordStrength"];
    });

    /** This will be called when the input receive and event of type focus or blur and will be used to control the label state
     * @param value Refers to the current content_provider coming from the input.
     * @param isFocused Receive true when the event type is focus and false when the event type is blur. */
    const toggleLabelClass = (value, isFocused: boolean): void => {
      isInputFocused.value = isFocused;
      if (value && !isFocused) {
        isInputFocused.value = true;
      }
    };

    /** Serves to handle when the input receive data coming from another source that do not come from user */
    watch(
      () => props.modelValue,
      async () => {
        if (props.modelValue) {
          isInputFocused.value = true;
          return;
        }
        await store.dispatch("authentication_module/clearPasswordStrength");
      }
    );

    const validatePasswordStrength = async (password: string) =>
      await store.dispatch(
        "authentication_module/validatePasswordStrength",
        password
      );

    /** Function responsible for checking if the password is weak.
     * For a password to be considered weak it must be below the entropy level. That would be 60.*/
    function passwordWeak(): boolean {
      return passwordStrength.value.entropy < passwordStrength.value.minEntropy;
    }

    /** Function responsible for checking if the password is good.
     * For a password to be considered good it needs to be 60 to 75 entropy.*/
    function passwordIntermediate(): boolean {
      return (
        passwordStrength.value.entropy >= passwordStrength.value.minEntropy &&
        passwordStrength.value.entropy <= 75
      );
    }

    /** Function responsible for checking if the password is strong.
     * For a password to be considered strong it must be above 75 entropy. */
    function passwordExcellent(): boolean {
      return passwordStrength.value.entropy > 75;
    }

    /** Function responsible for setting the input color according to entropy. */
    function updateInputColorBasedOnStrength(isPasswordValid: boolean): void {
      if (!isPasswordValid) {
        inputColor.value = invalidColor;
        return;
      }

      if (passwordStrength.value.entropy < passwordStrength.value.minEntropy) {
        inputColor.value = "#ffc800";
      }
      if (
        passwordStrength.value.entropy >= passwordStrength.value.minEntropy &&
        passwordStrength.value.entropy <= 75
      ) {
        inputColor.value = "#ff8d00";
      }
      if (passwordStrength.value.entropy > 75) {
        inputColor.value = successColor;
      }
    }

    const updatePasswordValidationDependencies = (password: string): void => {
      eightCharacterValidationPassed.value = password.length >= 8;
      noWhiteSpaceValidationPassed.value = !/\s/g.test(password);
      upperCaseValidationPassed.value = /(?=.*[A-Z])/.test(password);
      lowerCaseValidationPassed.value = /(?=.*[a-z])/.test(password);
      numericCharacterValidationPassed.value = /(?=.*[0-9])/.test(password);
      specialCharacterValidationPassed.value = /(?=.*[!@#$%^&*])/.test(
        password
      );
    };

    const isPasswordValid = (): boolean => {
      if (isDecolaTemplate()) {
        return (
          eightCharacterValidationPassed.value &&
          noWhiteSpaceValidationPassed.value &&
          upperCaseValidationPassed.value &&
          lowerCaseValidationPassed.value &&
          numericCharacterValidationPassed.value &&
          specialCharacterValidationPassed.value
        );
      } else {
        return (
          eightCharacterValidationPassed.value &&
          noWhiteSpaceValidationPassed.value &&
          lowerCaseValidationPassed.value &&
          specialCharacterValidationPassed.value
        );
      }
    };

    /** Function responsible for validating the password and passing it on to the registration modal.
     * @param password password entered by the user. */
    const onInput = async (password: string): Promise<void> => {
      updatePasswordValidationDependencies(password);

      const isPasswordStrong = strengthRegex.test(password);
      if (isPasswordStrong) {
        timerId = setTimeout(async () => {
          await validatePasswordStrength(password);
          updateInputColorBasedOnStrength(isPasswordValid());
          clearTimeout(timerId);
        }, 200);
      } else {
        await store.dispatch("authentication_module/clearPasswordStrength");
      }

      emit("update:modelValue", password);
    };

    /** Function responsible for making the password view span visible. */
    function showSpan(): string {
      if (props.passwordVisibility) {
        return isPasswordVisible.value ? "text" : "password";
      }
      return props.typeInput as string;
    }

    /** Function responsible for making the password visible. */
    function togglePassword(): void {
      isPasswordVisible.value = !isPasswordVisible.value;
    }

    return {
      isInputFocused,
      color,
      passwordStrength,
      noWhiteSpaceValidationPassed,
      eightCharacterValidationPassed,
      upperCaseValidationPassed,
      lowerCaseValidationPassed,
      numericCharacterValidationPassed,
      specialCharacterValidationPassed,
      isPasswordVisible,
      toggleLabelClass,
      passwordWeak,
      passwordIntermediate,
      passwordExcellent,
      validatePasswordStrength,
      onInput,
      showSpan,
      togglePassword,
      isDecolaTemplate,
      isConstrucolorTemplate,
      isCirculoTemplate
    };
  }
};

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "e8953702": (_ctx.color)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__